import {ArrowPathIcon, TrashIcon} from "@heroicons/react/24/outline";
import Switch from "../../../../../components/general/Switch.tsx";
import {Tag} from "../../../../../components/general/Tag.tsx";
import {MoneyLabel} from "../../../../../components/general/MoneyLabel.tsx";
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import Loading from "../../../../../components/general/Loading.tsx";
import {RecurringSeriesDetails} from "./types/recurringInvoice.ts";
import {cadenceLabel, formatDateNL} from "./utils/recurringInvoiceFormat.ts";

interface Props {
    series: RecurringSeriesDetails[]
    isLoading: boolean
    busySeriesIds: string[]
    onOpenPreview: (series: RecurringSeriesDetails) => void
    onToggleActive: (series: RecurringSeriesDetails, isActive: boolean) => void
    onDelete: (series: RecurringSeriesDetails) => void
    onCreateNew: () => void
}

function EmptyState({onCreateNew}: { onCreateNew: () => void }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-50">
                <ArrowPathIcon className="h-6 w-6 text-gray-400"/>
            </div>
            <div>
                <p className="text-sm font-medium text-gray-900">Nog geen terugkerende facturen</p>
                <p className="mt-1 text-sm text-gray-500">
                    Maak een reeks aan om facturen automatisch op een vast ritme klaar te zetten.
                </p>
            </div>
            <SecondaryButton title="Nieuwe reeks" onClick={onCreateNew}/>
        </div>
    )
}

export function RecurringInvoicesList({
    series,
    isLoading,
    busySeriesIds,
    onOpenPreview,
    onToggleActive,
    onDelete,
    onCreateNew,
}: Props) {
    if (isLoading) {
        return (
            <div className="flex justify-center py-16">
                <Loading message="Terugkerende facturen laden..."/>
            </div>
        )
    }

    if (series.length === 0) {
        return <EmptyState onCreateNew={onCreateNew}/>
    }

    return (
        <ul className="divide-y divide-gray-200 rounded-xl border border-gray-200 bg-white">
            {series.map((row) => {
                const isActive = !row.is_paused
                const isBusy = busySeriesIds.includes(row.id)

                return (
                    <li key={row.id} className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50">
                        <button
                            type="button"
                            onClick={() => onOpenPreview(row)}
                            className="flex min-w-0 flex-1 items-center gap-4 text-left"
                            aria-label={`Bekijk de volgende factuur voor ${row.customer_name}`}
                        >
                            <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-gray-900">{row.customer_name}</p>
                                <p className="truncate text-sm text-gray-500">{row.description}</p>
                            </div>

                            <Tag variant="primary" size="xs">{cadenceLabel(row.cadence)}</Tag>

                            <div className="hidden w-28 shrink-0 text-right sm:block">
                                <p className="font-medium text-gray-900">
                                    <MoneyLabel
                                        value={row.total_excluding_vat_cents / 100}
                                        showCurrency
                                        currency={row.currency}
                                    />
                                </p>
                                <p className="text-xs text-gray-500">excl. btw</p>
                            </div>

                            <div className="hidden w-32 shrink-0 text-sm sm:block">
                                {isActive ? (
                                    <>
                                        <p className="text-gray-900">{formatDateNL(row.next_run_on)}</p>
                                        <p className="text-xs text-gray-500">volgende factuur</p>
                                    </>
                                ) : (
                                    <p className="text-gray-400">Gepauzeerd</p>
                                )}
                            </div>
                        </button>

                        <div className="flex shrink-0 items-center gap-3">
                            <Switch
                                checked={isActive}
                                disabled={isBusy}
                                onChange={(checked: boolean) => onToggleActive(row, checked)}
                                aria-label={isActive
                                    ? `Pauzeer de reeks voor ${row.customer_name}`
                                    : `Activeer de reeks voor ${row.customer_name}`}
                            />
                            <button
                                type="button"
                                onClick={() => onDelete(row)}
                                disabled={isBusy}
                                className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                aria-label={`Verwijder de reeks voor ${row.customer_name}`}
                            >
                                <TrashIcon className="h-5 w-5"/>
                            </button>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}
